<template>
  <div>
    <h1>Knowledge Documents Management</h1>

    <div id="buttons-div">
      <div class="form-group">
        <label class="form-label" for="chatbot">Chatbot</label>
        <select id="chatbot" v-model="selectedChatbotId" @change="fetchFiles" class="input-field">
          <option v-for="chatbot in chatbots" :key="chatbot.id" :value="chatbot.id">
            {{ chatbot.name }}
          </option>
        </select>
      </div>

      <button class="pill-button ghost" id="refreshButton" @click="fetchFiles">Refresh</button>
      <button class="pill-button accent" id="uploadButton" @click="triggerFileInput">Upload File</button>
    </div>

    <input type="file" id="fileInput" ref="fileInput" class="hidden" @change="handleFileUpload"
      accept=".pdf,.doc,.docx,.txt,.md" />

    <table id="fileTable">
      <thead>
        <tr>
          <th class="hidden">ID</th>
          <th class="hidden">Chatbot ID</th>
          <th>File Name</th>
          <th>Stored Name</th>
          <th>Upload Date</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="file in files" :key="file.id">
          <td class="hidden">{{ file.id }}</td>
          <td class="hidden">{{ file.chatbot_id }}</td>
          <td>{{ file.original_name }}</td>
          <td>{{ decodeStoredName(file.stored_name) }}</td>
          <td>{{ file.upload_date }}</td>
          <td>
            <button class="pill-button ghost" @click="deleteFile(file.id)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="files.length === 0" id="noRecordsMessage">No records found.</p>

    <!-- Spinner overlay while loading -->
    <Spinner :show="isLoading" />

  </div>
</template>

<script>
import Spinner from '../components/SpinnerComponent.vue';

export default {
  name: "ManageKnowledgeDocumentsPage",
  components: {
    Spinner,
  },
  data() {
    return {
      apiUrl: process.env.VUE_APP_MICROSERVICE_API_URL,
      userId: localStorage.getItem('userId') || null,
      chatbots: JSON.parse(localStorage.getItem('chatbotRecords')) || [],
      selectedChatbotId: null,
      files: [],
      isLoading: false, // To track loading state
    };
  },
  methods: {
    async fetchFiles() {
      this.isLoading = true;
      try {
        const response = await fetch(`${this.apiUrl}/chatbots/${this.selectedChatbotId}/knowledgebases`);
        const data = await response.json();
        this.files = data || [];
      } catch (error) {
        console.error('Error fetching files:', error);
      }
      this.isLoading = false;
    },
    async deleteFile(fileId) {
      this.isLoading = true;
      try {
        await fetch(`${this.apiUrl}/knowledgebases/${fileId}`, { method: 'DELETE' });
        this.fetchFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
      }
      this.isLoading = false;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    async handleFileUpload(event) {
      this.isLoading = true;
      const file = event.target.files[0];
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('chatbot_id', this.selectedChatbotId);

        try {
          await fetch(`${this.apiUrl}/knowledgebases`, {
            method: 'POST',
            body: formData,
          });
          this.fetchFiles();
        } catch (error) {
          console.error('Error uploading file:', error);
        }
      }
      this.isLoading = false;
      // Clear the file input value
      event.target.value = '';
    },
    decodeStoredName(name) {
      const parser = new DOMParser();
      return parser.parseFromString(`<!doctype html><body>${name}`, 'text/html').body.textContent;
    },
  },
  mounted() {
    // console.log('Chatbots:', this.chatbots);
    if (this.chatbots.length > 0) {
      this.selectedChatbotId = this.chatbots[0].id;
      // console.log('Selected Chatbot ID:', this.selectedChatbotId);
      this.fetchFiles();
    }
  },
};
</script>

<style scoped>
.main-container {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  color: var(--text-primary);
}

h1 {
  font-size: 2rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
  text-align: center;
}

#buttons-div {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 10px;
}

.table-shell {
  background: var(--bg-glass);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(10px);
  overflow: hidden;
  margin-top: 20px;
}

#fileTable {
  width: 100%;
  border-collapse: collapse;
}

#fileTable th {
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-primary);
  padding: 12px;
  text-align: left;
  font-weight: 600;
}

#fileTable th,
#fileTable td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 12px 14px;
}

#fileTable tbody tr:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* Hidden Columns */
.hidden {
  display: none;
}

#noRecordsMessage {
  margin-top: 20px;
  text-align: center;
  color: var(--text-secondary);
}

@media (max-width: 768px) {
  .main-container {
    padding: 16px;
  }

  #fileTable th,
  #fileTable td {
    padding: 10px;
  }
}
</style>
